import EditIcon from "@mui/icons-material/Edit";
import { Box, IconButton, Tooltip } from "@mui/material";
import { ChangeEvent, useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { CompanyAvatarView } from "./CompanyAvatarView";
import { getCompanyAvatarSrc } from "../helpers/getCompanyAvatarSrc";
import { getCompanyInitial } from "../helpers/getCompanyInitial";
import { colors } from "../../../ui/colors";
import { useUploadCompanyAvatar } from "../hooks/useUploadCompanyAvatar";
import { LibraryIconsAvatar } from "./LibraryIconsAvatar";
import { ArchiveIconAvatar } from "./ArchiveIconsAvatar";

type EditableCompanyAvatarProps = {
  company?: {
    businessName?: string | null;
    name?: string | null;
    avatar?: string | null;
    logoUrl?: string | null;
    avatarUrl?: string | null;
    imageUrl?: string | null;
    logo?: string | null;
    role?: {
      name?: string | null;
      type?: string | null;
    } | null;
  } | null;
  size?: number;
  onOpenLibrary?: () => void;
  onLibraryOpenChange?: (open: boolean) => void;
};

export const EditableCompanyAvatar = ({ company, size = 48, onOpenLibrary, onLibraryOpenChange }: EditableCompanyAvatarProps) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [previewSrc, setPreviewSrc] = useState<string | undefined>();
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const { mutate: uploadCompanyAvatar, isPending } = useUploadCompanyAvatar();

  const src = previewSrc || getCompanyAvatarSrc(company);
  const initial = getCompanyInitial(company);
  const alt = company?.businessName || company?.name || "Company";
  const companyName = company?.businessName || company?.name || "Company";
  const companyRole = company?.role?.name || company?.role?.type || "Owner";

  useEffect(() => {
    return () => {
      if (previewSrc) URL.revokeObjectURL(previewSrc);
    };
  }, [previewSrc]);

  const setPendingAvatarFile = (file: File) => {
    const nextPreviewSrc = URL.createObjectURL(file);
    setPreviewSrc((currentPreviewSrc) => {
      if (currentPreviewSrc) URL.revokeObjectURL(currentPreviewSrc);
      return nextPreviewSrc;
    });
    setPendingFile(file);
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setPendingAvatarFile(file);

    event.target.value = "";
  };

  const handleCancelAvatarChange = () => {
    setPendingFile(null);
    setPreviewSrc((currentPreviewSrc) => {
      if (currentPreviewSrc) URL.revokeObjectURL(currentPreviewSrc);
      return undefined;
    });
    handleCloseMenu();
  };

  const handleConfirmAvatarChange = () => {
    if (!pendingFile) return;

    uploadCompanyAvatar(
      { file: pendingFile },
      {
        onSuccess: () => {
          setPendingFile(null);
          handleCloseMenu();
        },
        onError: () => {
          handleCancelAvatarChange();
        },
      },
    );
  };

  // abrir ventana emergente para la biblioteca
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const isMenuOpen = Boolean(menuAnchorEl);

  const handOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    flushSync(() => {
      onOpenLibrary?.();
    });
    onLibraryOpenChange?.(true);
    setMenuAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    onLibraryOpenChange?.(false);
    setMenuAnchorEl(null);
  };

  const handleOpenFilePicker = () => {
    flushSync(() => {
      onOpenLibrary?.();
    });
    window.setTimeout(() => {
      inputRef.current?.click();
    }, 0);
  };

  const handleSelectLibraryAvatar = async (avatar: ArchiveIconAvatar) => {
    try {
      // Convierte el icono de la biblioteca en archivo para usar el mismo guardado.
      const response = await fetch(avatar.src);
      const blob = await response.blob();
      const extension = blob.type === "image/webp" ? "webp" : blob.type === "image/svg+xml" ? "svg" : "png";
      const file = new File([blob], `${avatar.name}.${extension}`, { type: blob.type || "image/png" });

      setPendingAvatarFile(file);
    } catch (error) {
      console.error("Error selecting library avatar:", error);
    }
  };

  return (
    <>
      <Box
        data-company-avatar-editor="true"
        onClick={(event) => event.stopPropagation()}
        onMouseDown={(event) => event.stopPropagation()}
        sx={{
          position: "relative",
          width: size,
          height: size,
          "&:hover .company-avatar-edit-button": {
            opacity: 1,
            transform: "scale(1)",
          },
        }}
      >
        <CompanyAvatarView src={src} initial={initial} size={size} alt={alt} />

        <Tooltip title="Edit company profile" arrow>
          <Box
            component="span"
            sx={{ display: "inline-flex" }}
            onClick={(event) => event.stopPropagation()}
            onMouseDown={(event) => event.stopPropagation()}
          >
            <IconButton
              size="small"
              className="company-avatar-edit-button"
              aria-label="Edit company profile"
              onClick={handOpenMenu}
              onMouseDown={(event) => event.stopPropagation()}
              disabled={isPending}
              sx={{
                position: "absolute",
                right: -4,
                bottom: -4,
                width: 22,
                height: 22,
                bgcolor: "var(--fx-surface-base, #FFFFFF)",
                border: `1px solid ${colors.orange[100]}`,
                boxShadow: "0 2px 6px rgba(0,0,0,0.14)",
                color: colors.orange.base,
                opacity: 0,
                transform: "scale(0.86)",
                transition: "opacity 0.15s ease, transform 0.15s ease, background-color 0.15s ease",
                "&:hover": {
                  bgcolor: colors.orange[50],
                },
                "&.Mui-disabled": {
                  bgcolor: colors.gray[50],
                  color: colors.gray[300],
                },
              }}
            >
              <EditIcon sx={{ fontSize: 13 }} />
            </IconButton>
          </Box>
        </Tooltip>

        <LibraryIconsAvatar
          open={isMenuOpen}
          anchorEl={menuAnchorEl}
          disabled={isPending}
          onChangeAvatar={handleOpenFilePicker}
          onDropAvatar={setPendingAvatarFile}
          onSelectAvatar={handleSelectLibraryAvatar}
          hasPendingAvatar={Boolean(pendingFile)}
          isSaving={isPending}
          onSave={handleConfirmAvatarChange}
          onCancel={handleCancelAvatarChange}
          avatarSrc={src}
          avatarInitial={initial}
          avatarAlt={alt}
          companyName={companyName}
          companyRole={companyRole}
        />

        <input
          ref={inputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          hidden
          onClick={(event) => event.stopPropagation()}
          onChange={handleFileChange}
        />
      </Box>
    </>
  );
};
